import * as $ from 'jquery';

import { AttributeError, AttributeInvalidError, AttributeRequiredError } from './attribute-error';
import { GetIwControllerFn, JQueryPluginExtras, JQueryPluginFn, JQueryPluginPreInitFn } from './custom-component-types'

const DATA_CONTROLLER = 'iw-component';

const CUSTOM_COMPONENT_ATTR = 'iw-component';
const CUSTOM_COMPONENT_SELECTOR = `[${CUSTOM_COMPONENT_ATTR}]`;

let instanceId = 0;

declare global {
  interface JQuery {
    getIwController<TComponent extends CustomComponent<TElement>, TElement extends HTMLElement = HTMLElement>(): TComponent;
  }
}

export interface CustomComponent<TElement extends HTMLElement = HTMLElement> {
  // provides correct typing of "this.constructor" from within the CustomComponent class
  constructor: typeof CustomComponent;
}

function getIwControllerFn(ctrlClass: typeof CustomComponent): GetIwControllerFn {
  return function getIwController<TComponent extends CustomComponent<TElement>,
    TElement extends HTMLElement = HTMLElement>() {
    return ctrlClass.getController<TComponent, TElement>(this.filter(CUSTOM_COMPONENT_SELECTOR).add(this.find(CUSTOM_COMPONENT_SELECTOR)));
  }
}

/**
 * A base class that handles shared functionality for custom components
 */
export class CustomComponent<TElement extends HTMLElement = HTMLElement> {

  public static readonly CUSTOM_COMPONENT_ATTR = CUSTOM_COMPONENT_SELECTOR;

  /**
   * Returns a selector string that can be used to find elements bound to a {@link CustomComponent} instance.
   *
   * The iw-component attribute is automatically added to any component using {@link CustomComponent} so they
   * can be easily identified.
   */
  public static readonly CUSTOM_COMPONENT_SELECTOR = CUSTOM_COMPONENT_SELECTOR;

  /**
   * Extend jQuery with the component's jQuery plugin function.
   */
  public static loadPlugin(): void {
    if (!$.fn.getIwController) {
      $.fn.getIwController = CustomComponent.jQueryComponentControllerFn();
    }
  }

  /**
   * Overridden to return a CSS selector for the host element of the component.
   *
   * @abstract
   */
  public static COMPONENT_SELECTOR: string = '';

  public static get ID_PREFIX() { return this.getIdPrefix(); }

  protected static getIdPrefix() {
    return this.COMPONENT_SELECTOR;
  }

  protected static readonly TEMPLATE: string;

  public static getController<
    TComponent extends CustomComponent<TElement>,
    TElement extends HTMLElement = HTMLElement
  >($el: JQuery<TElement>): TComponent {
    return $el.data(DATA_CONTROLLER);
  }

  public static bind<
    TComponent extends CustomComponent<TElement>,
    TElement extends HTMLElement = HTMLElement
  >($el: JQuery<TElement>): TComponent {
    return this.getController<TComponent, TElement>($el) || new this($el) as TComponent;
  }

  public static jQueryPlugin<
    TExtras extends JQueryPluginExtras,
    TElement extends HTMLElement = HTMLElement
  >(controllerName: string, extras?: TExtras, preInit?: JQueryPluginPreInitFn<TElement>): JQueryPluginFn<TElement> & TExtras {
    return Object.assign(this.jQueryInitFn<TElement>(preInit), {
      [controllerName]: this,
      COMPONENT_SELECTOR: this.COMPONENT_SELECTOR,
      CUSTOM_COMPONENT_ATTR: this.CUSTOM_COMPONENT_ATTR,
      CUSTOM_COMPONENT_SELECTOR: this.CUSTOM_COMPONENT_SELECTOR,
    }, extras);
  }

  /**
   * Returns a function that can act as an initializer for the component
   */
  protected static jQueryInitFn<TElement extends HTMLElement = HTMLElement>(
    preInit?: JQueryPluginPreInitFn<TElement>,
  ): (this: JQuery<TElement>) => JQuery<TElement> {
    const ctrlClass = this;
    return function initCustomComponent(this: JQuery<TElement>) {
      if (typeof preInit === 'function') {
        preInit.call(this);
      }
      return this.filter(ctrlClass.COMPONENT_SELECTOR)
        .add(this.find(ctrlClass.COMPONENT_SELECTOR))
        .each((index, el) => { ctrlClass.bind($(el)); });
    };
  }

  public static jQueryComponentControllerFn() {
    return getIwControllerFn(this);
  }

  public readonly el: TElement;
  public readonly id: string;

  protected readonly idPrefix: string;

  constructor(public readonly $el: JQuery<TElement>) {
    /**
     * The JQuery-wrapped component host element
     * @type {JQuery}
     */
    this.$el = $el;
    /**
     * The native host element
     * @type {HTMLElement}
     */
    this.el = $el[0];
    this.idPrefix = this.constructor.ID_PREFIX;
    this.id = this.$el.prop('id') || `${this.idPrefix}--${instanceId++}`;

    this.$el
      .prop('id', this.id)
      .attr(CUSTOM_COMPONENT_ATTR, '')
      .data(DATA_CONTROLLER, this);

    if (this.constructor.TEMPLATE) {
      this.$el.html(this.constructor.TEMPLATE);
    }
  }

  /**
   * Returns a CSS selector style string with the tag name and ID representing the component host element.
   *
   * Example: iw-component#some-id--1
   */
  public getDebugIdentifier(): string {
    return this.getDebugIdentifierById();
  }

  protected getDebugIdentifierById(): string {
    const tag = this.$el.prop('tagName').toLocaleLowerCase();
    const id = this.$el.attr('id');
    return `${tag}#${id}`;
  }

  protected attributeError(attributeName: string, message: string, required: boolean = false): AttributeError {
    return Object.assign(
      new Error(`The ${required ? "required " : ""}"${attributeName}" attribute on element "${this.getDebugIdentifier()}" ${message}`),
      {
        name: 'AttributeError' as const,
        attributeName,
        attributeRequired: false,
        element: this.el,
      },
    );
  }

  protected attributeInvalidError(attributeName: string, reason: string, value: any): AttributeInvalidError {
    return Object.assign(
      this.attributeError(attributeName, `is invalid: ${reason}`),
      {
        name: 'AttributeInvalidError' as const,
        reason,
        value,
      },
    );
  }

  protected requiredAttributeError(attributeName: string): AttributeRequiredError {
    return Object.assign(
      this.attributeError(attributeName, 'is missing', true),
      {
        name: 'AttributeRequiredError' as const,
        required: true,
      },
    );
  }
}
